// `DECLARE <name>[, <name>…] ▮` — the TYPE slot of a procedural local
// declaration.
//
// The grammar projection's data-type slot (`editorContextSlot.mysql.ts`'s
// `isMysqlDataTypeSlot`) recognises the routine HEADER's parameter types: it
// requires the caret to sit inside the `CREATE PROCEDURE|FUNCTION (` param
// list. A `DECLARE` inside the routine's — or an EVENT's — BODY has no such
// paren, so that caret fell to `unknown`: the type names reached the widget
// only as tier-7 ambient keywords, and the whole relation list came with them
// (mysql coverage script.39 §2 forbids `<any-table>` there).
//
// MySQL-only, deliberately (parity rule): Oracle spells the local's type after
// `<name>` too but also admits `%TYPE` / `%ROWTYPE` anchors that its own
// resolver already answers at that caret; T-SQL's `DECLARE @v ▮` is covered by
// the mssql data-type slot (the `@` sigil makes it unambiguous); PG declares
// locals inside a `DECLARE` SECTION rather than a statement, so the tail shape
// this module keys on does not occur.
//
// Known imprecision, documented rather than guessed at: `DECLARE c ▮CURSOR
// FOR …` reaches the same shape, where `CURSOR` — not a type — is what
// belongs. Deciding between the two needs the text AFTER the caret; the type
// vocabulary is the overwhelmingly common case and is what the coverage specs
// pin (script.33 §7/§8, 42 §3, 45 §4, 46 §2).

import type { DatabaseEngineId } from '@/core/engines';

import { dropAttachedPartialTokens, tokenize, type Token } from './tokenizer';

/** `DECLARE {CONTINUE|EXIT|UNDO} HANDLER FOR …` — a handler declaration, whose
 *  next token is `HANDLER`, never a type. */
const HANDLER_ACTIONS: ReadonlySet<string> = new Set(['CONTINUE', 'EXIT', 'UNDO']);

/**
 * True when the caret sits where a procedural local's data type belongs: the
 * statement is a `DECLARE`, and everything between it and the caret is the
 * comma-separated list of names being declared.
 */
export function isAtDeclareTypeSlot(
  text: string,
  offset: number,
  statementStart: number,
  engine: DatabaseEngineId,
): boolean {
  if (engine !== 'mysql') return false;
  const tokens = dropAttachedPartialTokens(
    tokenize(text, statementStart, offset, engine),
    offset,
  );
  let i = tokens.length - 1;
  // At least one declared name must sit before the caret, and it must be a
  // bare/quoted identifier — a keyword-lexed word there is already the type
  // (`DECLARE v_x INT ▮` is past this slot, not in it).
  if (i < 1 || tokens[i]!.type !== 'ident') return false;
  if (HANDLER_ACTIONS.has(wordOf(tokens[i]) ?? '')) return false;
  i -= 1;
  while (i >= 0 && tokens[i]!.type === 'comma') {
    i -= 1;
    if (i < 0 || tokens[i]!.type !== 'ident') return false;
    i -= 1;
  }
  return wordOf(tokens[i]) === 'DECLARE';
}

function wordOf(t: Token | undefined): string | null {
  if (!t) return null;
  if (t.type !== 'ident' && t.type !== 'kw') return null;
  return t.text.toUpperCase();
}
